import * as React from "react";
import { TreeView } from "@mui/x-tree-view/TreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useCallback, memo } from "react";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { VscMarkdown, VscSettingsGear } from "react-icons/vsc";
import { MdExpandMore, MdChevronRight } from "react-icons/md";

interface Page {
  index: number;
  name: string;
  route: string;
}

interface Props {
  pages: {
    index: number;
    name: string;
    route: string;
  }[];
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  currentComponent: string;
  setCurrentComponent: React.Dispatch<React.SetStateAction<string>>;
  visiblePageIndexs: number[];
  setVisiblePageIndexs: React.Dispatch<React.SetStateAction<number[]>>;
  /** Called after a page is picked (used to close the mobile drawer). */
  onItemSelected?: () => void;
}

function AppTree({
  pages,
  selectedIndex,
  setSelectedIndex,
  currentComponent,
  setCurrentComponent,
  visiblePageIndexs,
  setVisiblePageIndexs,
  onItemSelected,
}: Props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)");
  let { pathname } = useLocation();

  const page: Page = pages.find((x) => x.route === pathname)!;

  useEffect(() => {
    if (page) {
      setSelectedIndex(page.index);
    }
  }, [page, setSelectedIndex]);

  const v = theme.palette.vscode;

  const getTreeItemBgColor = useCallback(
    (index: number) => {
      return selectedIndex === index ? v.treeItemBgSelected : v.treeItemBg;
    },
    [v, selectedIndex]
  );

  const getTreeItemColor = useCallback(
    (index: number) => {
      return selectedIndex === index && currentComponent === "tree"
        ? v.treeItemTextSelected
        : v.treeItemText;
    },
    [v, selectedIndex, currentComponent]
  );

  const handleHomeClick = useCallback(() => {
    navigate("/");
    setSelectedIndex(-1);
    onItemSelected?.();
  }, [navigate, setSelectedIndex, onItemSelected]);

  const handleItemClick = useCallback(
    (index: number, route: string) => {
      if (!visiblePageIndexs.includes(index)) {
        const newIndexs = [...visiblePageIndexs, index];
        setVisiblePageIndexs(newIndexs);
      }
      navigate(route);
      setSelectedIndex(index);
      setCurrentComponent("tree");
      onItemSelected?.();
    },
    [
      visiblePageIndexs,
      setVisiblePageIndexs,
      navigate,
      setSelectedIndex,
      setCurrentComponent,
      onItemSelected,
    ]
  );

  const treeItems = useMemo(
    () =>
      pages.map(({ index, name, route }) => {
        const getFileIcon = () => {
          if (route === "/settings") {
            return <VscSettingsGear color={v.treeFileIcon} />;
          }
          return <VscMarkdown color={v.treeFileIcon} />;
        };

        return (
          <TreeItem
            key={index}
            nodeId={index.toString()}
            label={name}
            sx={{
              color: getTreeItemColor(index),
              backgroundColor: getTreeItemBgColor(index),
              "&& .Mui-selected": {
                backgroundColor: getTreeItemBgColor(index),
              },
            }}
            icon={getFileIcon()}
            onClick={() => handleItemClick(index, route)}
          />
        );
      }),
    [pages, v, getTreeItemColor, getTreeItemBgColor, handleItemClick]
  );

  return (
    <TreeView
      aria-label="file system navigator"
      defaultCollapseIcon={<MdExpandMore />}
      defaultExpandIcon={<MdChevronRight />}
      sx={{ minWidth: isMobile ? 170 : 220 }}
      defaultExpanded={["-1"]}
    >
      <TreeItem
        nodeId="-1"
        label="Home"
        color={v.treeHomeLabel}
        onClick={handleHomeClick}
      >
        {treeItems}
      </TreeItem>
    </TreeView>
  );
}

export default memo(AppTree);
